import { c as _c } from "react/compiler-runtime";
import { Card, CardContent, Divider, Stack, styled, Switch, Tooltip, Typography, type Theme } from '@mui/material';
import type { Condition, BackupPolicyConfig } from '@repo/api-gw-sdk';
import { BackupPolicyType, BackupStatus, ConditionOperatorEnum } from '@repo/api-gw-sdk';
import { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@/components/accordion';
import { createBackupStatusNotInCondition } from '@/components/backupStatus/createBackupStatusNotInCondition';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { ConfirmationDialog } from '@/components/dialogs/confirmationDialog';
import { Loader } from '@/components/layout/loading';
import { OptionMenu } from '@/components/option-menu/option-menu';
import { backupPolicies } from '@/components/queryBuilder/properties';
import { getResourcesScopeType, type ResourcesScopeType } from '@/components/resourcesSelection/resourceSelectionDynamicViewer';
import { ResourcesSelectionViewer } from '@/components/resourcesSelection/resourcesSelectionViewer';
import { NavigationLink } from '@/components/shared/link';
import { OverflowTooltip } from '@/components/tooltips/overflowTooltip';
import { AccountAsVaultTag, VaultTag } from '@/components/vaults/vaultTag';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import type { BackupVault } from '@/data/vaults/backupVault';
import { createCombineCondition } from '@/types/advanceFilter';
import { pluralize } from '@/utils/string';
import { BackupPolicyConditionTitle } from './BackupPolicyConditionEditor';
import { GwsResourceSelectionViewer } from './GwsResourceSelectionViewer';
import { M365ResourceSelectionViewer } from './M365ResourceSelectionViewer';
import { backupPolicyNativeProvider, isContinuousPolicyType, isGoogleWorkspacePolicyType, isMicrosoft365PolicyType, isNativePolicyType, isPitrPolicyType, isSaasWorkspacePolicyType } from './backupPoliciesTypes';
import { useAllBackupPolicyProperties } from './backupPolicyProperties';
import { BackupPolicyContinuousSchedulesTable, BackupPolicySchedulesTable } from './backupPolicySchedulesTable';
import { BackupPolicyTypeTag } from './backupPolicyTypeTag';
import { BackupPolicyTypeDescription } from './customPolicySupportedDisclaimer';
export const BPCard = styled(Card)((props: {
  disabled: boolean;
  theme?: Theme;
  style?: object;
}) => {
  return {
    border: '1px solid var(--mui-palette-divider)',
    backgroundColor: props.disabled ? 'var(--mui-palette-background-default)' : 'var(--mui-palette-background-paper)',
    ...props.theme?.typography.body2,
    textAlign: 'left',
    color: props.theme?.palette.text.secondary,
    minWidth: '550px',
    minHeight: '170px',
    boxShadow: 'none',
    paddingTop: '12px',
    borderRadius: '16px',
    '& .MuiCardContent-root': {
      padding: '0px 24px 24px'
    },
    ...props.style
  };
});
interface BackupPolicyCardProps {
  processing?: boolean;
  policy: BackupPolicyConfig;
  vaults?: BackupVault[];
  defaultExpanded?: boolean;
  onEdit?: (policy: BackupPolicyConfig) => void;
  onDelete?: (policy: BackupPolicyConfig) => void;
  onDuplicate?: (policy: BackupPolicyConfig) => void;
  onEnabledPolicyUpdate?: (policy: BackupPolicyConfig) => void;
}
export function BackupPolicyCard(props) {
  const $ = _c(162);
  const enabled = props.policy.enabled;
  const {
    snackbar
  } = useWorkspace();
  const {
    isAuthorized
  } = useRoles();
  const allBackupPolicyProperties = useAllBackupPolicyProperties();
  const [vaultsSectionExpanded, setVaultsSectionExpanded] = useState(!!props.defaultExpanded);
  const [disablePolicyDialogOpen, setDisablePolicyDialogOpen] = useState(false);
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let t0;
  let t1;
  let t10;
  let t11;
  let t12;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let togglePolicyEnabledState;
  if ($[0] !== allBackupPolicyProperties || $[1] !== enabled || $[2] !== isAuthorized || $[3] !== props || $[4] !== snackbar || $[5] !== vaultsSectionExpanded) {
    const matchingVaults = props.vaults?.filter(vault => props.policy?.schedules.find(schedule => schedule.vaultId === vault.id)).slice(0, 2);
    let t13;
    if ($[25] !== props.policy.condition) {
      t13 = getResourcesScopeType(props.policy.condition, "allIncluded");
      $[25] = props.policy.condition;
      $[26] = t13;
    } else {
      t13 = $[26];
    }
    const resourcesScopeType = t13;
    let t14;
    if ($[27] !== props.policy.policyType) {
      t14 = isSaasWorkspacePolicyType(props.policy.policyType);
      $[27] = props.policy.policyType;
      $[28] = t14;
    } else {
      t14 = $[28];
    }
    const isSaasWorkspace = t14;
    let t15;
    if ($[29] !== props.policy.policyType) {
      t15 = isGoogleWorkspacePolicyType(props.policy.policyType);
      $[29] = props.policy.policyType;
      $[30] = t15;
    } else {
      t15 = $[30];
    }
    const isGoogleWorkspace = t15;
    let t16;
    if ($[31] !== props || $[32] !== snackbar) {
      t16 = () => {
        const enabled_0 = props.policy.enabled;
        props.onEnabledPolicyUpdate?.(props.policy);
        snackbar.showMessage(<><span className="font-semibold">{props.policy.name}</span> backup policy is now {enabled_0 ? "disabled" : "enabled"}.</>);
      };
      $[31] = props;
      $[32] = snackbar;
      $[33] = t16;
    } else {
      t16 = $[33];
    }
    togglePolicyEnabledState = t16;
    let t17;
    if ($[34] !== enabled || $[35] !== togglePolicyEnabledState) {
      t17 = () => {
        if (enabled) {
          setDisablePolicyDialogOpen(true);
        } else {
          togglePolicyEnabledState();
        }
      };
      $[34] = enabled;
      $[35] = togglePolicyEnabledState;
      $[36] = t17;
    } else {
      t17 = $[36];
    }
    const onToggleClicked = t17;
    const t18 = props.policy?.id || "";
    let T5;
    let T6;
    let t19;
    let t20;
    let t21;
    let t22;
    let t23;
    let t24;
    if ($[37] !== enabled || $[38] !== isAuthorized || $[39] !== isSaasWorkspace || $[40] !== props.onDelete || $[41] !== props.onDuplicate || $[42] !== props.onEdit || $[43] !== props.onEnabledPolicyUpdate || $[44] !== props.policy.attachedResources || $[45] !== props.policy.name || $[46] !== props.policy.policyType || $[47] !== props.processing || $[48] !== t18) {
      const inventoryFilterCondition = {
        operator: ConditionOperatorEnum.ContainsAnyOf,
        value: [t18],
        property: backupPolicies.name
      };
      T4 = BPCard;
      t11 = !enabled;
      t12 = "backup-policy-card";
      T6 = Stack;
      t22 = "row";
      if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
        t23 = {
          maxWidth: "100%",
          justifyContent: "space-between",
          gap: "12px",
          alignItems: "center",
          margin: "12px 24px"
        };
        $[60] = t23;
      } else {
        t23 = $[60];
      }
      let t25;
      if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
        t25 = {
          alignItems: "center",
          gap: "12px",
          minWidth: "0px",
          flexShrink: 1
        };
        $[61] = t25;
      } else {
        t25 = $[61];
      }
      let t26;
      if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
        t26 = {
          minWidth: "0px",
          flexShrink: 1
        };
        $[62] = t26;
      } else {
        t26 = $[62];
      }
      let t27;
      if ($[63] !== props.policy.name) {
        t27 = <Stack sx={t26}><OverflowTooltip text={props.policy.name} renderContent={_temp} /></Stack>;
        $[63] = props.policy.name;
        $[64] = t27;
      } else {
        t27 = $[64];
      }
      let t28;
      if ($[65] !== props.policy.policyType) {
        t28 = <BackupPolicyTypeTag policyType={props.policy.policyType} />;
        $[65] = props.policy.policyType;
        $[66] = t28;
      } else {
        t28 = $[66];
      }
      if ($[67] !== t27 || $[68] !== t28) {
        t24 = <Stack direction="row" sx={t25}>{t27}{t28}</Stack>;
        $[67] = t27;
        $[68] = t28;
        $[69] = t24;
      } else {
        t24 = $[69];
      }
      T5 = Stack;
      t19 = "row";
      if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
        t20 = {
          alignItems: "center",
          gap: "16px"
        };
        $[70] = t20;
      } else {
        t20 = $[70];
      }
      t21 = enabled && !isSaasWorkspace && <>{props.processing ? <Loader variant="inline" /> : <NavigationLink path="/inventory" searchParams={{
          query: createCombineCondition([inventoryFilterCondition, createBackupStatusNotInCondition([BackupStatus.ExcludedFromBackup, BackupStatus.Disconnected, BackupStatus.Unsupported, BackupStatus.LimitExceeded])])
        }} disabled={!isAuthorized("read:inventory")} className="text-(--mui-palette-primary-main)"><ButtonWithIcon text={`${props.policy.attachedResources ?? 0} ${pluralize("Resource", props.policy.attachedResources ?? 0)}`} iconPosition="right" icon="material-symbols-arrow-right-alt" /></NavigationLink>}{(props.onEdit && props.onDelete && props.onDuplicate || props.onEnabledPolicyUpdate) && <Divider sx={{
          height: "16px"
        }} orientation="vertical" />}</>;
      $[37] = enabled;
      $[38] = isAuthorized;
      $[39] = isSaasWorkspace;
      $[40] = props.onDelete;
      $[41] = props.onDuplicate;
      $[42] = props.onEdit;
      $[43] = props.onEnabledPolicyUpdate;
      $[44] = props.policy.attachedResources;
      $[45] = props.policy.name;
      $[46] = props.policy.policyType;
      $[47] = props.processing;
      $[48] = t18;
      $[49] = T4;
      $[50] = T5;
      $[51] = T6;
      $[52] = t11;
      $[53] = t12;
      $[54] = t19;
      $[55] = t20;
      $[56] = t21;
      $[57] = t22;
      $[58] = t23;
      $[59] = t24;
    } else {
      T4 = $[49];
      T5 = $[50];
      T6 = $[51];
      t11 = $[52];
      t12 = $[53];
      t19 = $[54];
      t20 = $[55];
      t21 = $[56];
      t22 = $[57];
      t23 = $[58];
      t24 = $[59];
    }
    let t25;
    if ($[71] !== isAuthorized || $[72] !== isGoogleWorkspace || $[73] !== isSaasWorkspace || $[74] !== props) {
      t25 = (isSaasWorkspace && props.onDelete || props.onEdit && props.onDelete && props.onDuplicate) && <OptionMenu options={[...(isSaasWorkspace ? isGoogleWorkspace ? [{
        id: "edit-policy",
        text: "Edit",
        menuItemProps: {
          disabled: !isAuthorized("update:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onEdit?.(props.policy);
          }
        }
      }, {
        id: "divider",
        divider: true
      }] : [] : [{
        id: "edit-policy",
        text: "Edit",
        menuItemProps: {
          disabled: !isAuthorized("update:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onEdit?.(props.policy);
          }
        }
      }, {
        id: "duplicate-policy",
        text: "Duplicate",
        menuItemProps: {
          disabled: !isAuthorized("create:policies"),
          onClick: () => {
            props.onDuplicate?.(props.policy);
          }
        }
      }, {
        id: "divider",
        divider: true
      }]), {
        id: "delete-policy",
        text: "Delete",
        menuItemProps: {
          disabled: !isAuthorized("delete:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onDelete?.(props.policy);
          }
        }
      }]} />;
      $[71] = isAuthorized;
      $[72] = isGoogleWorkspace;
      $[73] = isSaasWorkspace;
      $[74] = props;
      $[75] = t25;
    } else {
      t25 = $[75];
    }
    let t26;
    if ($[76] !== enabled || $[77] !== isAuthorized || $[78] !== onToggleClicked || $[79] !== props.onEnabledPolicyUpdate) {
      t26 = props.onEnabledPolicyUpdate && <Tooltip title={enabled ? "This policy is enabled" : "This policy is disabled"}><Switch disabled={!isAuthorized("update:policies")} checked={enabled} onChange={onToggleClicked} /></Tooltip>;
      $[76] = enabled;
      $[77] = isAuthorized;
      $[78] = onToggleClicked;
      $[79] = props.onEnabledPolicyUpdate;
      $[80] = t26;
    } else {
      t26 = $[80];
    }
    let t27;
    if ($[81] !== T5 || $[82] !== t19 || $[83] !== t20 || $[84] !== t21 || $[85] !== t25 || $[86] !== t26) {
      t27 = <T5 direction={t19} sx={t20}>{t21}{t25}{t26}</T5>;
      $[81] = T5;
      $[82] = t19;
      $[83] = t20;
      $[84] = t21;
      $[85] = t25;
      $[86] = t26;
      $[87] = t27;
    } else {
      t27 = $[87];
    }
    if ($[88] !== T6 || $[89] !== t22 || $[90] !== t23 || $[91] !== t24 || $[92] !== t27) {
      t10 = <T6 direction={t22} sx={t23}>{t24}{t27}</T6>;
      $[88] = T6;
      $[89] = t22;
      $[90] = t23;
      $[91] = t24;
      $[92] = t27;
      $[93] = t10;
    } else {
      t10 = $[93];
    }
    T3 = CardContent;
    if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        marginTop: "24px"
      };
      $[94] = t7;
    } else {
      t7 = $[94];
    }
    let t28;
    if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = <AccordionSummary><Typography variant="h4">{"Resource selection"}</Typography></AccordionSummary>;
      $[95] = t28;
    } else {
      t28 = $[95];
    }
    let t29;
    if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
      t29 = {
        gap: "24px"
      };
      $[96] = t29;
    } else {
      t29 = $[96];
    }
    let t30;
    if ($[97] !== allBackupPolicyProperties || $[98] !== props.policy.condition || $[99] !== props.policy.exclude || $[100] !== props.policy.include || $[101] !== props.policy.policyType || $[102] !== props.policy.resourceTypes || $[103] !== resourcesScopeType) {
      t30 = isGoogleWorkspacePolicyType(props.policy.policyType) ? <GwsResourceSelectionViewer resourceId={props.policy.include?.[0]} /> : isMicrosoft365PolicyType(props.policy.policyType) ? <M365ResourceSelectionViewer resourceId={props.policy.include?.[0]} /> : <ResourcesSelectionViewer condition={props.policy.condition} conditionTitle={<BackupPolicyConditionTitle resourceTypes={props.policy.resourceTypes} policyType={props.policy.policyType ?? BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD} />} include={props.policy.include ?? []} exclude={props.policy.exclude ?? []} resourcesScopeType={resourcesScopeType} dynamicResourcesHeader={null} allExcludedDescription={"This backup policy won\u2019t be automatically applied to any resources. Only manually included resources will be assigned to the policy."} allIncludedDescription={isPitrPolicyType(props.policy.policyType) ? "This backup policy will be automatically applied to all resources of the selected type. When new ones are discovered, they\u2019ll be included too." : "This backup policy will be automatically applied to all resources. When new ones are discovered, they\u2019ll be included too."} supportProperties={allBackupPolicyProperties.flat()} selectedResourceTypes={props.policy.resourceTypes} policyType={props.policy.policyType} />;
      $[97] = allBackupPolicyProperties;
      $[98] = props.policy.condition;
      $[99] = props.policy.exclude;
      $[100] = props.policy.include;
      $[101] = props.policy.policyType;
      $[102] = props.policy.resourceTypes;
      $[103] = resourcesScopeType;
      $[104] = t30;
    } else {
      t30 = $[104];
    }
    let t31;
    if ($[105] !== props.policy.policyType) {
      t31 = <BackupPolicyTypeDescription policyType={props.policy.policyType} showForPitr={false} />;
      $[105] = props.policy.policyType;
      $[106] = t31;
    } else {
      t31 = $[106];
    }
    let t32;
    if ($[107] !== t30 || $[108] !== t31) {
      t32 = <AccordionDetails><Stack sx={t29}>{t30}{t31}</Stack></AccordionDetails>;
      $[107] = t30;
      $[108] = t31;
      $[109] = t32;
    } else {
      t32 = $[109];
    }
    if ($[110] !== props.defaultExpanded || $[111] !== t32) {
      t8 = <Accordion defaultExpanded={props.defaultExpanded}>{t28}{t32}</Accordion>;
      $[110] = props.defaultExpanded;
      $[111] = t32;
      $[112] = t8;
    } else {
      t8 = $[112];
    }
    if ($[113] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <Divider sx={{
        margin: "12px -24px"
      }} />;
      $[113] = t9;
    } else {
      t9 = $[113];
    }
    T2 = Accordion;
    t5 = vaultsSectionExpanded;
    if ($[114] !== vaultsSectionExpanded) {
      t6 = () => setVaultsSectionExpanded(!vaultsSectionExpanded);
      $[114] = vaultsSectionExpanded;
      $[115] = t6;
    } else {
      t6 = $[115];
    }
    T1 = AccordionSummary;
    T0 = Stack;
    t0 = "row";
    t1 = "w-full";
    if ($[116] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        justifyContent: "space-between",
        alignItems: "center"
      };
      t3 = <Typography variant="h4">Backup schedules</Typography>;
      $[116] = t2;
      $[117] = t3;
    } else {
      t2 = $[116];
      t3 = $[117];
    }
    t4 = !vaultsSectionExpanded && <div>{isNativePolicyType(props.policy.policyType) ? <AccountAsVaultTag cloudProvider={backupPolicyNativeProvider(props.policy)} region={props.policy.schedules?.[0]?.region || "Source region"} /> : !!matchingVaults?.length && matchingVaults.map(_temp2)}</div>;
    $[0] = allBackupPolicyProperties;
    $[1] = enabled;
    $[2] = isAuthorized;
    $[3] = props;
    $[4] = snackbar;
    $[5] = vaultsSectionExpanded;
    $[6] = T0;
    $[7] = T1;
    $[8] = T2;
    $[9] = T3;
    $[10] = T4;
    $[11] = t0;
    $[12] = t1;
    $[13] = t10;
    $[14] = t11;
    $[15] = t12;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = togglePolicyEnabledState;
  } else {
    T0 = $[6];
    T1 = $[7];
    T2 = $[8];
    T3 = $[9];
    T4 = $[10];
    t0 = $[11];
    t1 = $[12];
    t10 = $[13];
    t11 = $[14];
    t12 = $[15];
    t2 = $[16];
    t3 = $[17];
    t4 = $[18];
    t5 = $[19];
    t6 = $[20];
    t7 = $[21];
    t8 = $[22];
    t9 = $[23];
    togglePolicyEnabledState = $[24];
  }
  let t13;
  if ($[118] !== T0 || $[119] !== t0 || $[120] !== t1 || $[121] !== t2 || $[122] !== t3 || $[123] !== t4) {
    t13 = <T0 direction={t0} className={t1} sx={t2}>{t3}{t4}</T0>;
    $[118] = T0;
    $[119] = t0;
    $[120] = t1;
    $[121] = t2;
    $[122] = t3;
    $[123] = t4;
    $[124] = t13;
  } else {
    t13 = $[124];
  }
  let t14;
  if ($[125] !== T1 || $[126] !== t13) {
    t14 = <T1>{t13}</T1>;
    $[125] = T1;
    $[126] = t13;
    $[127] = t14;
  } else {
    t14 = $[127];
  }
  let t15;
  if ($[128] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      paddingRight: 0,
      paddingTop: "24px"
    };
    $[128] = t15;
  } else {
    t15 = $[128];
  }
  let t16;
  if ($[129] !== props.policy || $[130] !== props.vaults) {
    t16 = <AccordionDetails sx={t15}>{isContinuousPolicyType(props.policy.policyType) ? <BackupPolicyContinuousSchedulesTable policy={props.policy} /> : <BackupPolicySchedulesTable policy={props.policy} vaults={props.vaults} />}</AccordionDetails>;
    $[129] = props.policy;
    $[130] = props.vaults;
    $[131] = t16;
  } else {
    t16 = $[131];
  }
  let t17;
  if ($[132] !== T2 || $[133] !== t14 || $[134] !== t16 || $[135] !== t5 || $[136] !== t6) {
    t17 = <T2 expanded={t5} onChange={t6}>{t14}{t16}</T2>;
    $[132] = T2;
    $[133] = t14;
    $[134] = t16;
    $[135] = t5;
    $[136] = t6;
    $[137] = t17;
  } else {
    t17 = $[137];
  }
  let t18;
  if ($[138] !== T3 || $[139] !== t17 || $[140] !== t7 || $[141] !== t8 || $[142] !== t9) {
    t18 = <T3 sx={t7}>{t8}{t9}{t17}</T3>;
    $[138] = T3;
    $[139] = t17;
    $[140] = t7;
    $[141] = t8;
    $[142] = t9;
    $[143] = t18;
  } else {
    t18 = $[143];
  }
  let t19;
  if ($[144] !== props.policy.name) {
    t19 = <>The <span className="font-semibold">{props.policy.name}</span>{" "}backup policy will stop being applied to resources. Existing snapshots will be kept until their retention expires.</>;
    $[144] = props.policy.name;
    $[145] = t19;
  } else {
    t19 = $[145];
  }
  let t20;
  if ($[146] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => setDisablePolicyDialogOpen(false);
    $[146] = t20;
  } else {
    t20 = $[146];
  }
  let t21;
  if ($[147] !== togglePolicyEnabledState) {
    t21 = () => {
      togglePolicyEnabledState();
      setDisablePolicyDialogOpen(false);
    };
    $[147] = togglePolicyEnabledState;
    $[148] = t21;
  } else {
    t21 = $[148];
  }
  let t22;
  if ($[149] !== disablePolicyDialogOpen || $[150] !== t19 || $[151] !== t21) {
    t22 = <ConfirmationDialog isOpen={disablePolicyDialogOpen} title="Disable backup policy" description={t19} onCancel={t20} onConfirm={t21} buttonTitle="Disable Policy" />;
    $[149] = disablePolicyDialogOpen;
    $[150] = t19;
    $[151] = t21;
    $[152] = t22;
  } else {
    t22 = $[152];
  }
  let t23;
  if ($[153] !== t10 || $[154] !== t18 || $[155] !== t22) {
    t23 = <>{t10}{t18}{t22}</>;
    $[153] = t10;
    $[154] = t18;
    $[155] = t22;
    $[156] = t23;
  } else {
    t23 = $[156];
  }
  let t24;
  if ($[157] !== T4 || $[158] !== t11 || $[159] !== t12 || $[160] !== t23) {
    t24 = <T4 disabled={t11} data-testid={t12}>{t23}</T4>;
    $[157] = T4;
    $[158] = t11;
    $[159] = t12;
    $[160] = t23;
    $[161] = t24;
  } else {
    t24 = $[161];
  }
  return t24;
}
function _temp2(vault_0) {
  return <VaultTag key={vault_0.id} vault={vault_0} />;
}
function _temp(text, ref) {
  return <Typography noWrap={true} variant="h3" ref={ref}>{text}</Typography>;
}